 
import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const alertVariants = cva(
  "relative w-full rounded-[var(--radius-xxl)] border px-[var(--space-lg)] py-[var(--space-md)] text-sm shadow-[var(--shadow-xs)]",
  {
    variants: {
      variant: {
        default: "border-border/70 bg-background/80 text-foreground",
        muted: "border-border/50 bg-muted/40 text-foreground/90",
        info: "border-[color:var(--token-accent-info-border)] bg-[color:var(--token-accent-info-surface)] text-[color:var(--token-accent-info-foreground-muted)] dark:text-[color:var(--token-accent-info-foreground-muted)]",
        success: "border-[color:var(--token-accent-success-border)] bg-[color:var(--token-accent-success-surface)] text-[color:var(--token-accent-success-foreground-muted)] dark:text-[color:var(--token-accent-success-foreground-muted)]",
        warning:
          "border-[color:var(--token-accent-warning-border-strong)] bg-[color:var(--token-accent-warning-surface)] text-[color:var(--token-accent-warning-foreground-muted)]",
        destructive: "border-destructive/50 bg-destructive/10 text-destructive",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

type AlertProps = React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant, role = "alert", ...props }, ref) => (
    <div
      ref={ref}
      role={role}
      data-slot="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  ),
)
Alert.displayName = "Alert"

const AlertTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>( 
  ({ className, ...props }, ref) => (
    <h5
      ref={ref}
      data-slot="alert-title"
      className={cn("text-base font-semibold leading-tight tracking-tight text-current", className)}
      {...props}
    />
  ),
)
AlertTitle.displayName = "AlertTitle"

const AlertDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>( 
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      data-slot="alert-description"
      className={cn("text-sm text-current/90 leading-relaxed", className)}
      {...props}
    />
  ),
)
AlertDescription.displayName = "AlertDescription"

export { Alert, AlertTitle, AlertDescription }
